// First-party QLink funnel events (EVENTS-CONTRACT 2026-09-29 §3/§5).
//
// Six drawer steps that otherwise live only in the dataLayer are posted to
// POST /qlearn/qlink/questions/{qlinkId}/funnel-event, which always answers
// 204. Everything here is fire-and-forget and invisible: no DOM, no state, no
// awaiting, and nothing thrown back into a UX path.
//
// Privacy: the body carries only the event name, the allowlisted props, the
// QLink platform label, the question id and a random per-page-load visitId.
// It never reads or sends the share token (userAcquisitiontoken), the URL, an
// email/phone/name or a user id; the server takes the user from the Bearer.
import url from '../constants/urlList';
import { detectInAppBrowser } from './in-app-browser';

export const QLINK_FUNNEL_EVENT = Object.freeze({
  TEASER_SHOWN: 'teaser_shown',
  AUTH_START: 'auth_start',
  AUTH_RESULT: 'auth_result',
  INAPP_ESCAPE_CLICKED: 'inapp_escape_clicked',
  EXPLANATION_VIEWED_AFTER_SIGNIN: 'explanation_viewed_after_signin',
  START_PRACTICING_CLICKED: 'start_practicing_clicked',
});

// Sent at most once per page load for the same link, question and props.
const ONCE_PER_VISIT = new Set([
  QLINK_FUNNEL_EVENT.TEASER_SHOWN,
  QLINK_FUNNEL_EVENT.INAPP_ESCAPE_CLICKED,
  QLINK_FUNNEL_EVENT.EXPLANATION_VIEWED_AFTER_SIGNIN,
  QLINK_FUNNEL_EVENT.START_PRACTICING_CLICKED,
]);

// The contract's `app` enum; anything else is left out rather than sent.
const APP_VALUES = new Set([
  'instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'snapchat', 'line',
  'threads', 'pinterest', 'android_webview', 'none',
]);

const VISIT_ID_BYTES = 16;

let visitIdResolved = false;
let visitId = null;
let signedInThisVisit = false;
const sentOnce = new Set();

// Test seam: { source } replaces window.crypto (redefining window.crypto is not
// reliable across the Node/jsdom versions CI and local runs use).
let cryptoOverride = null;
export const setQLinkCryptoForTests = (source) => {
  if (process.env.NODE_ENV !== 'test') return;
  cryptoOverride = { source };
};

const randomHex = () => {
  const source = cryptoOverride
    ? cryptoOverride.source
    : (typeof window !== 'undefined' ? window.crypto : undefined);
  if (!source || typeof source.getRandomValues !== 'function') return null;
  const bytes = new Uint8Array(VISIT_ID_BYTES);
  source.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

// 32 lower-case hex characters, made once per page load and never persisted.
// Omitted from the body when crypto is unavailable.
export const getQLinkVisitId = () => {
  if (!visitIdResolved) {
    visitIdResolved = true;
    try {
      visitId = randomHex();
    } catch (e) {
      visitId = null;
    }
  }
  return visitId;
};

// The session JWT only; a missing, blank or stringified-null value sends no
// Authorization header from here.
const readSessionToken = () => {
  try {
    const token = window.localStorage.getItem('token');
    if (typeof token !== 'string') return null;
    const trimmed = token.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    return token;
  } catch (e) {
    return null;
  }
};

const safeDetect = () => {
  try {
    return detectInAppBrowser();
  } catch (e) {
    return null;
  }
};

// `browser` (in_app | browser) and `app` for a detection result; detects now
// when the caller has none. Not in an in-app browser → app "none".
export const inAppFunnelProps = (detected = safeDetect()) => {
  const inApp = !!(detected && detected.inApp);
  const props = { browser: inApp ? 'in_app' : 'browser' };
  if (!inApp) {
    props.app = 'none';
  } else if (detected && APP_VALUES.has(detected.app)) {
    props.app = detected.app;
  }
  return props;
};

export const browserDimension = (detected) => inAppFunnelProps(detected).browser;

// A drawer sign-in (Google popup success or phone verify success) completed
// during THIS page load.
export const markQLinkSignInThisVisit = () => {
  signedInThisVisit = true;
};

export const signinDimension = () => (signedInThisVisit ? 'this_visit' : 'earlier');

const stringProps = (props) => {
  const clean = {};
  if (!props || typeof props !== 'object') return clean;
  Object.keys(props).forEach((key) => {
    const value = props[key];
    if (typeof value === 'string' && value) clean[key] = value;
  });
  return clean;
};

const latchKey = (event, qlinkId, questionId, props) => [
  event,
  qlinkId,
  questionId || '',
  Object.keys(props).sort((a, b) => a.localeCompare(b)).map((key) => `${key}=${props[key]}`).join('&'),
].join('|');

export const recordQLinkFunnelEvent = ({ qlinkId, questionId, platform, event, props } = {}) => {
  try {
    if (typeof qlinkId !== 'string' || !qlinkId || typeof event !== 'string' || !event) {
      return undefined;
    }
    const cleanProps = stringProps(props);
    const cleanQuestionId = typeof questionId === 'string' && questionId ? questionId : null;
    if (ONCE_PER_VISIT.has(event)) {
      const key = latchKey(event, qlinkId, cleanQuestionId, cleanProps);
      if (sentOnce.has(key)) return undefined;
      sentOnce.add(key);
    }
    const body = {
      event,
      platform: typeof platform === 'string' && platform ? platform : 'unknown',
      props: cleanProps,
    };
    if (cleanQuestionId) body.questionId = cleanQuestionId;
    const id = getQLinkVisitId();
    if (id) body.visitId = id;

    const headers = { 'Content-Type': 'application/json' };
    const token = readSessionToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    if (typeof fetch !== 'function') return undefined;
    // Plain fetch, never the axios instance; the response is ignored.
    const pending = fetch(`${url.qlinkQues}/${encodeURIComponent(qlinkId)}/funnel-event`, {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify(body),
    });
    Promise.resolve(pending).catch(() => {});
  } catch (e) {
    // Instrumentation must never break the page.
  }
  return undefined;
};

// Test-only: a fresh "page load" (new visitId, empty once-latch, no sign-in).
export const resetQLinkFunnelEventsForTests = () => {
  cryptoOverride = null;
  visitIdResolved = false;
  visitId = null;
  signedInThisVisit = false;
  sentOnce.clear();
};
